// src/config/interceptorsConfig.ts

import Vue from 'vue';
import { AxiosError } from 'axios';
import { extractErrorMessage, CustomError } from '../utils/errorHandler';

/**
 * Initializes Axios interceptors for handling requests and responses globally.
 *
 * @param _store - The Vuex store instance.
 * @param _router - The Vue Router instance.
 */
export default function (
  _store: typeof import('../store/store').default,
  _router: typeof import('../router').default,
) {
  let numberOfAjaxCallPending = 0;

  // Flag to prevent multiple redirects
  let isRedirecting = false;

  // Intercept all AJAX requests
  Vue.axios.interceptors.request.use(
    function (config: any) {
      numberOfAjaxCallPending++;

      // Retrieve the center object from Vuex store
      const center = _store.getters['navigation/globalCenter'];

      if (center && center.id) {
        // Ensure config.params is an object
        config.params = config.params || {};

        // Check if center-id related params are already set
        const hasCenterId =
          config.params['center-id'] ||
          config.params['center_id'] ||
          config.params['centerId'];

        if (!hasCenterId) {
          config.params = {
            ...config.params,
            'center-id': center.id,
            center_id: center.id,
            centerId: center.id,
          };
        }

        // If the request method is POST or PUT, also add center-id to the request body
        if (
          (config.method === 'post' || config.method === 'put') &&
          config.data
        ) {
          config.data = {
            ...config.data,
            'center-id': center.id,
            center_id: center.id,
            centerId: center.id,
          };
        }
      }

      _store.dispatch('navigation/appLoading');
      return config;
    },
    function (error: any) {
      // Handle request error
      numberOfAjaxCallPending--;

      if (numberOfAjaxCallPending <= 0) {
        _store.dispatch('navigation/stopAppLoading');
      }

      _store.commit('messages/ADD_MESSAGE', error);
      return Promise.reject(error);
    },
  );

  // Intercept all AJAX responses
  Vue.axios.interceptors.response.use(
    function (response: any) {
      numberOfAjaxCallPending--;

      if (numberOfAjaxCallPending <= 0) {
        _store.dispatch('navigation/stopAppLoading');
      }
      return response;
    },
    async function (err: AxiosError) {
      numberOfAjaxCallPending--;

      if (numberOfAjaxCallPending <= 0) {
        _store.dispatch('navigation/stopAppLoading');
      }

      if (err.response && err.response.status === 401) {
        // Prevent multiple redirects
        if (!isRedirecting) {
          isRedirecting = true;

          await _store.dispatch('auth/processLogout');

          const path = '/login';
          if (_router.currentRoute.path !== path) {
            try {
              await _router.push(path);
            } catch (navigationError) {
              // Type guard to ensure navigationError has a 'name' property
              if (
                navigationError instanceof Error &&
                navigationError.name !== 'NavigationDuplicated'
              ) {
                console.error('Router push error:', navigationError);
              }
              // If it's a NavigationDuplicated error, do nothing
            }
          }

          isRedirecting = false;
        }

        // Resolve the promise to prevent further error handling
        return Promise.resolve({});
      }

      const customError: CustomError = extractErrorMessage(err);

      if (customError.message) {
        _store.dispatch('messages/addErrorMessage', customError.message);
      }

      return Promise.reject(customError);
    },
  );
}
